import { SubmitHandler, useForm } from 'react-hook-form';
import { StepProps } from '@pages/auth/register';
import { userInfoSchema, UserInfo } from '@interfaces/auth';
import { useEffect, useState } from 'react';
import { Button } from '@vibTheme/components/_base/Button';
import {
  InputExternalValidation,
  InputPhone,
  InputText,
} from '@vibTheme/components/_base/Input';
import { Typography } from '@vibTheme/components/_base/Typography';
import { isExistsByValid } from '@interfaces/profile';
import { useExternalValidationResolver } from '@hooks/useExternalValidationResolver';
import { InputCheckbox } from '@vibTheme/components/_base/Input/InputCheckbox';
import {
  findPrivacyPolicyDetails,
  findTermsOfUseDetails,
} from '@api/userAgreements';
import { sendRdEventPersonalData } from '@api/rdStation';
import { createLead } from '@api/crm';

export function UserInfoStep({
  queryParams,
  stateStorage,
  publish,
}: StepProps) {
  const [privacyPolicyLink, setPrivacyPolicyLink] = useState('');
  const [termsOfLink, setTermsOfLink] = useState('');
  const {
    externalValidationResolver,
    isLoading: isExternalValidationLoading,
    isValid: isExternalValidationValid,
  } = useExternalValidationResolver(userInfoSchema, [
    { name: 'email', validate: isExistsByValid('email') },
  ]);

  const {
    control,
    handleSubmit,
    formState: { errors, isValidating },
  } = useForm<UserInfo>({
    resolver: externalValidationResolver,
    mode: 'onSubmit',
  });

  const fetchTermsOfUseDetails = async () => {
    const response = await findTermsOfUseDetails();
    setTermsOfLink(response.link);
  };

  const fetchPrivacyPolicyDetails = async () => {
    const response = await findPrivacyPolicyDetails();
    setPrivacyPolicyLink(response.link);
  };

  useEffect(() => {
    fetchTermsOfUseDetails();
    fetchPrivacyPolicyDetails();
  }, []);

  const onSubmit: SubmitHandler<UserInfo> = async (data) => {
    const personalData = {
      name: data.name,
      email: data.email,
      phone: String(data.phone),
    };

    void sendRdEventPersonalData(personalData).catch(console.error);
    void createLead(personalData).catch(console.error);

    publish({ ...stateStorage, ...data });
  };

  return (
    <form
      id="register-form-userinfo"
      className="flex flex-col w-full text-left gap-main"
      onSubmit={handleSubmit(onSubmit)}
    >
      <InputText
        id="register-input-name"
        name="name"
        control={control}
        label="Nome completo/Razão social"
        placeholder="ex: seu nome completo"
        defaultValue={stateStorage?.name ?? queryParams?.name}
      />
      <InputText
        name="preferredName"
        control={control}
        label="Nome social (opcional)"
        placeholder="Nome social (opcional)"
        defaultValue={stateStorage?.preferredName ?? ''}
      />
      <InputExternalValidation
        id="register-input-email"
        type="email"
        name="email"
        control={control}
        label="E-mail"
        placeholder="ex: seuemail@vibx.com.br"
        defaultValue={stateStorage?.email ?? queryParams?.email}
        isLoading={isExternalValidationLoading.email}
        isValid={isExternalValidationValid.email}
      />
      <InputPhone
        id="register-input-phone"
        name="phone"
        control={control}
        defaultValue={
          stateStorage?.phone ||
          (queryParams?.phone ? '+55' + queryParams?.phone : '')
        }
        error={!!errors.phone}
        helperText={errors.phone?.message}
      />
      <InputCheckbox
        id="register-terms-of-use"
        name="termsOfUse"
        control={control}
        defaultValue={stateStorage?.termsOfUse}
        label={
          <Typography className="mr-1 text-gray-500">
            Li e aceito os{' '}
            <a
              id="terms-of-use-link"
              href={termsOfLink}
              className="text-primary hover:text-primary"
              target="_blank"
            >
              Termos de Uso{' '}
            </a>
            e{' '}
            <a
              id="privacy-policy-link"
              href={privacyPolicyLink}
              className="text-primary hover:text-primary"
              target="_blank"
            >
              Política de Privacidade
            </a>
          </Typography>
        }
      />
      <Button
        id="register-button-submit-userinfo"
        type="submit"
        disabled={isValidating}
      >
        Próximo
      </Button>
    </form>
  );
}
